import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Verificar se o token é válido
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await api.get('/users/me', { timeout: 60000 });
      setUser(response.data);
    } catch (error) {
      console.error('Erro ao buscar informações do usuário:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      // OAuth2 password: corpo deve ser x-www-form-urlencoded (não multipart FormData)
      const body = new URLSearchParams();
      body.set('username', username);
      body.set('password', password);

      const response = await api.post('/token', body, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        timeout: 60000,
      });

      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      
      // Buscar informações do usuário
      await fetchUserInfo();
      
      return { success: true };
    } catch (error) {
      console.error('Erro no login:', error);
      
      let errorMessage = 'Erro no login';
      
      if (error.response?.status === 401) {
        errorMessage = 'Usuário ou senha inválidos';
      } else if (error.response?.status === 422) {
        errorMessage = 'Dados inválidos';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/register', userData);
      return { success: true, user: response.data };
    } catch (error) {
      console.error('Erro no cadastro:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erro no cadastro' 
      };
    }
  };

  const value = {
    user,
    login,
    logout,
    register,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    isGroupAdmin: !!(user?.is_group_admin),
    adminGroupIds: user?.admin_group_ids || [],
    canManageUsers: !!(user?.is_admin || user?.is_group_admin),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
